import { useCallback } from 'react';
import { log } from '@/logger/logger';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { useWorkflowStore } from '@/state/workflow.state';
import type { NodeId } from 'web';

const logger = log.getLogger('useNodeSetAsCover');

/**
 * Sets the workflow poster from the current {@link NodeViewer} image output for `nodeId`
 * (file URL or WASM export on click only — no per-render export).
 * The returned `canSetPoster` is true when the node's evaluated output is an image.
 */
export function useNodeSetAsCover(
  nodeId: NodeId,
  analyticsNodeType?: string,
): { canSetPoster: boolean; generateNodePreviewImageAndSetPoster: () => Promise<void> } {
  const flowView = useFlowView();
  const setRecipePoster = useWorkflowStore((s) => s.setRecipePoster);
  const { track } = useAnalytics();

  const canSetPoster = flowView.evaluateNode(nodeId)?.type === 'image';

  const generateNodePreviewImageAndSetPoster = useCallback(async () => {
    try {
      const posterUrl = flowView.resolveNodePreviewImagePosterUrl(nodeId);
      if (!posterUrl) {
        return;
      }
      track(
        'node_image_set_as_cover_clicked',
        analyticsNodeType ? { sourceNodeType: analyticsNodeType } : {},
        TrackTypeEnum.BI,
      );
      await setRecipePoster(posterUrl);
    } catch (error) {
      logger.error('error saving workflow cover from node preview', error);
    }
  }, [analyticsNodeType, flowView, nodeId, setRecipePoster, track]);

  return { canSetPoster, generateNodePreviewImageAndSetPoster };
}
